import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Component, ComponentFactoryResolver, Inject, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Announcement, AppNotification } from '@icc/shared/helpers/b2b';
import { ToastComponent } from '../toast/toast.component';
import { AnnouncementService } from './announcement.service';
import { DialogService } from '@icc/ui';
import { AnnouncementPreviewComponent } from '../announcement-preview/announcement-preview.component';
import { map } from 'rxjs';
import { UserBoxData } from '@icc/shared/user-box-data';

@Component({
    selector: 'icc-announcements',
    templateUrl: './announcements.component.html',
    styleUrls: ['./announcements.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AnnouncementsComponent implements OnInit {
    protected containerElement: HTMLElement;

    public userData: UserBoxData = {
        userId: 0,
        userName: '',
        email: '',
        avatar: '',
        activePanelName: '',
        activeCompanyName: null,
        activeCompanyId: null,
        panels: [],
        editUserDataURL: null,
        markets: [],
        isLicenced: false,
        activeMarketId: null,
        activeScope: null,
        activePriceVersionId: null,
        activeProductBaseId: null,
        productBases: null,
        priceVersions: null,
        userBoxBasesAndPricesVisible: false,
        productBasesVisible: false,
        actualPriceVersionId: null,
        envDbVersion: null,
        hostname: window.location.origin,
        languages: [],
        activeLanguage: null,
    };

    announcements$ = this.announcementService.announcements$;
    badge$ = this.announcements$.pipe(map(announcements => announcements.some(el => el.highlighted) ? '!' : announcements.length));

    @Input() hideButton = false;
    @Input()
    set data(_data: UserBoxData | string) {
        if (typeof _data === 'string') {
            this.userData = JSON.parse(_data);
        } else {
            this.userData = _data;
        }
    }

    constructor(
        private announcementService: AnnouncementService,
        private overlay: Overlay,
        private componentFactoryResolver: ComponentFactoryResolver,
        @Inject(DOCUMENT) private document: Document,
        private injector: Injector,
        private dialogService: DialogService,
    ) {}

    ngOnInit(): void {
        this.announcementService.start(this.userData.userId, this.userData.envDbVersion);

        const containerClass = 'icc-toast-container';
        const container = this.document.createElement('div');
        container.classList.add(containerClass);
        container.classList.add('icc-toast-top-right');
        this.document.body.appendChild(container);
        this.containerElement = container;

        const appRef = this.injector.get<ApplicationRef>(ApplicationRef);
        const outlet = new DomPortalOutlet(container, this.componentFactoryResolver, appRef, this.injector, this.document);
        const portal = outlet.attach(new ComponentPortal(ToastComponent));
        portal.instance.onClose.subscribe(result => {
            this.onToastClose(result)
        });
    }

    onCloseIconClick(event: Event, message: Announcement) {
        if (message.pinned) {
            return;
        }
        this.announcementService.close(message);
        event.preventDefault();
        event.stopPropagation();
    }

    onToastClose(event: {
        message: AppNotification
    }) {
        this.announcementService.notificationClosed(event.message);
    }

    getIcon(message: AppNotification) {
        switch (message.severity) {
            case 'success':
                return 'done';
            case 'warn':
                return 'warning';
            case 'error':
                return 'close';
            case 'question':
                return 'edit';
            case 'info':
            default:
                return 'info';
        }
    }

    openMessageDetails(message: Announcement) {
        this.dialogService.open(AnnouncementPreviewComponent, {
            data: {
                announcement: message,
            }
        });
    }

    byUuid(index: number, message: Announcement) {
        return message.uuid;
    }

    ngOnDestroy() {
        const container = this.containerElement;

        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
    }
}
